import { useState, useEffect } from "react";
import { voteAPI, inviteAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import {
  Users, UserCheck, Megaphone, TrendingUp, Headphones, DollarSign,
  Settings, Monitor, Lightbulb, Shield, Compass, Check, Loader2,
} from "lucide-react";

const DEPT_ICONS = {
  megaphone: Megaphone, "trending-up": TrendingUp, headphones: Headphones,
  "dollar-sign": DollarSign, users: Users, settings: Settings,
  monitor: Monitor, lightbulb: Lightbulb, shield: Shield, compass: Compass,
};

const AVATAR_COLORS = [
  "bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500",
  "bg-cyan-500", "bg-violet-500", "bg-orange-500", "bg-teal-500",
];

const getAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const DepartmentAssignment = ({ open, onOpenChange, board, onUpdate }) => {
  const [assignments, setAssignments] = useState({});
  const [participants, setParticipants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState("by-user");

  useEffect(() => {
    if (!open || !board) return;

    const pList = (board.participants || []).filter((p) => p.role !== "owner" || true);
    setParticipants(pList);

    // Build current assignments map: { userId: Set(deptId) }
    const map = {};
    pList.forEach((p) => {
      map[p.user_id] = new Set(p.assigned_departments || []);
    });
    setAssignments(map);
  }, [open, board]);

  const toggleAssignment = (userId, deptId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      const userSet = new Set(next[userId] || []);
      if (userSet.has(deptId)) {
        userSet.delete(deptId);
      } else {
        userSet.add(deptId);
      }
      next[userId] = userSet;
      return next;
    });
  };

  const assignAll = (userId) => {
    setAssignments((prev) => ({
      ...prev,
      [userId]: new Set(board.departments.filter((d) => d.is_applicable).map((d) => d.id)),
    }));
  };

  const clearAll = (userId) => {
    setAssignments((prev) => ({ ...prev, [userId]: new Set() }));
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      const promises = Object.entries(assignments).map(([userId, deptSet]) =>
        voteAPI.assignDepartments(board.id, { user_id: userId, department_ids: [...deptSet] })
      );
      await Promise.all(promises);
      toast.success("Department assignments saved!");
      onUpdate?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save assignments");
    } finally {
      setSaving(false);
    }
  };

  if (!board) return null;

  const departments = board.departments.filter((d) => d.is_applicable);

  // Stats
  const totalAssigned = Object.values(assignments).reduce((sum, s) => sum + s.size, 0);
  const usersWithAssignments = Object.values(assignments).filter((s) => s.size > 0).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] p-0" data-testid="dept-assignment-dialog">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-indigo-500" />
              Assign Departments to Participants
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 mt-1">
            Assign participants to specific departments. They'll focus on their assigned areas during the game.
          </p>

          <div className="flex items-center gap-3 mt-3">
            <Badge variant="outline" className="text-xs">
              {usersWithAssignments}/{participants.length} users assigned
            </Badge>
            <Badge variant="outline" className="text-xs">
              {totalAssigned} total assignments
            </Badge>
          </div>

          <Tabs value={activeView} onValueChange={setActiveView} className="mt-3">
            <TabsList className="grid w-full grid-cols-2 h-9">
              <TabsTrigger value="by-user" className="text-xs" data-testid="view-by-user">By Participant</TabsTrigger>
              <TabsTrigger value="by-dept" className="text-xs" data-testid="view-by-dept">By Department</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <ScrollArea className="max-h-[50vh] px-6 py-4">
          {activeView === "by-user" ? (
            <div className="space-y-4">
              {participants.map((p) => {
                const userDepts = assignments[p.user_id] || new Set();
                return (
                  <div key={p.user_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`user-assign-${p.user_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${getAvatarColor(p.name)}`}>
                          {(p.name || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <span className="text-sm font-medium text-slate-800">{p.name}</span>
                          <span className="text-[10px] text-slate-400 ml-2">{p.email}</span>
                        </div>
                        {p.role === "owner" && <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[9px]">Facilitator</Badge>}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="ghost" size="sm" onClick={() => assignAll(p.user_id)} className="h-6 text-[10px] text-indigo-600 hover:bg-indigo-50">
                          All
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => clearAll(p.user_id)} className="h-6 text-[10px] text-slate-400 hover:bg-slate-50">
                          None
                        </Button>
                        <Badge variant="outline" className="text-[10px] font-mono">{userDepts.size}/{departments.length}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {departments.map((dept) => {
                        const Icon = DEPT_ICONS[dept.icon] || Settings;
                        const isChecked = userDepts.has(dept.id);
                        return (
                          <button
                            key={dept.id}
                            onClick={() => toggleAssignment(p.user_id, dept.id)}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-colors text-xs ${
                              isChecked
                                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                                : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                            }`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                              isChecked ? "bg-indigo-500 border-indigo-500" : "border-slate-300"
                            }`}>
                              {isChecked && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            <Icon className="w-3 h-3 shrink-0" />
                            <span className="truncate">{dept.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {departments.map((dept) => {
                const Icon = DEPT_ICONS[dept.icon] || Settings;
                const assignedUsers = participants.filter((p) => (assignments[p.user_id] || new Set()).has(dept.id));
                return (
                  <div key={dept.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`dept-assign-${dept.id}`}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-heading font-semibold text-sm text-slate-900">{dept.name}</h4>
                        <span className="text-[10px] text-slate-400">{assignedUsers.length} assigned</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {participants.map((p) => {
                        const isAssigned = (assignments[p.user_id] || new Set()).has(dept.id);
                        return (
                          <button
                            key={p.user_id}
                            onClick={() => toggleAssignment(p.user_id, dept.id)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs transition-colors ${
                              isAssigned
                                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                                : "border-slate-200 bg-white text-slate-400 hover:border-slate-300"
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white ${getAvatarColor(p.name)}`}>
                              {(p.name || "?")[0].toUpperCase()}
                            </div>
                            <span className="truncate max-w-[80px]">{p.name}</span>
                            {isAssigned && <Check className="w-3 h-3 text-indigo-500" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="px-6 py-4 border-t border-slate-100">
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="save-assignments-btn"
              onClick={saveAssignments}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving...</> : <><UserCheck className="w-4 h-4 mr-1.5" />Save Assignments</>}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DepartmentAssignment;
